/*
  <addon-picker> — product add-on block, browser behaviour.
  Markup comes from snippets/addon-block.liquid and snippets/addon-card.liquid,
  rendered by snippets/addon-resolver.liquid. Spec: mms-shopify-dashboard/
  docs/product-addons/matching-spec.md ("Building the add-on list", browser part).

  Public API (used by the cart module):
    picker.getSelections()  -> [{ productId, variantId }] for checked, visible cards.
                               variantId is the card's selected variant.
    picker.clearSelections()   Unchecks every card.
    picker.markFailed(variantId)
                               Unchecks and greys out the card offering that variant.
    picker.refresh()        -> Promise. Re-fetches /cart.js and re-applies in-cart hiding
                               (fallback only: normally the cart arrives with mms:cart-ready /
                               mms:cart-changed from assets/mms-cart.js, with no extra read).
    picker.setMessage(text)    Shows (or, with '', hides) the block's inline message.
    picker.titleForVariant(variantId) -> the card title offering that variant.

  Find the instance with document.querySelector('addon-picker').

  Add to Cart integration (bottom of this file): a capture-phase submit listener on
  document takes over the product form's submit only when that form belongs to a picker
  with ticked add-ons. Otherwise Impulse's own handler runs untouched. Cart requests go
  through window.MMSCart (assets/mms-cart.js).
*/
(function () {
  'use strict';

  if (customElements.get('addon-picker')) {
    return;
  }

  // assets/mms-cart.js reads the cart once on load (mms:cart-ready) and once per actual
  // change (mms:cart-changed), whatever caused it, and passes it in detail.cart.
  var CART_EVENTS = ['mms:cart-ready', 'mms:cart-changed'];
  // If the cart module never reports (not loaded, or its read failed), read it ourselves.
  var INITIAL_CART_FALLBACK_MS = 3000;

  function cartUrl() {
    var routes = window.theme && window.theme.routes;
    return (routes && routes.cart) || '/cart.js';
  }

  function shuffle(list) {
    var a = list.slice();
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = a[i];
      a[i] = a[j];
      a[j] = t;
    }
    return a;
  }

  customElements.define(
    'addon-picker',
    class AddonPicker extends HTMLElement {
      connectedCallback() {
        if (this._connected) return;
        this._connected = true;

        this.maxShown = parseInt(this.dataset.maxShown, 10) || 4;
        this.order = this.dataset.order === 'random' ? 'random' : 'fixed';
        this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-addon-card]'));
        // Fixed: DOM order (visible first, then buffer). Random: shuffled once per page view.
        this.sequence = this.order === 'random' ? shuffle(this.cards) : this.cards.slice();
        this.inCartProductIds = new Set();
        this.mainAvailable = this.dataset.mainAvailable !== 'false';
        this.oneTimeNote = this.querySelector('[data-addon-onetime]');
        this.blockEl = this.closest('.product-block--addons') || this;

        this._bindCards();
        this._bindCartEvents();
        this._bindVariantChange();
        this._bindSubscription();

        this._apply();
        // The initial display is settled once the first in-cart check has resolved
        // (random selection already ran in _apply). Only then may an impression go out.
        var self = this;
        var last = window.MMSCart && window.MMSCart.getLastCart && window.MMSCart.getLastCart();
        if (last) {
          this._applyCart(last);
          this._armImpression();
        } else {
          this._fallbackTimer = setTimeout(function () {
            if (!self._cartSeen) self.refresh().then(function () { self._armImpression(); });
          }, INITIAL_CART_FALLBACK_MS);
        }
      }

      disconnectedCallback() {
        var self = this;
        CART_EVENTS.forEach(function (name) {
          document.removeEventListener(name, self._onCartEvent);
        });
        if (this._variantContainer) {
          this._variantContainer.removeEventListener('variantChange', this._onVariantChange);
        }
        if (this._formObserver) this._formObserver.disconnect();
        if (this._impressionObserver) this._impressionObserver.disconnect();
        if (this._form) this._form.removeEventListener('change', this._onFormChange);
        clearTimeout(this._fallbackTimer);
        this._connected = false;
      }

      // ---------- Public API ----------

      getSelections() {
        return this.cards
          .filter(function (card) {
            var box = card.querySelector('[data-addon-checkbox]');
            return !card.hidden && box && box.checked && !box.disabled;
          })
          .map(function (card) {
            return {
              productId: Number(card.dataset.productId),
              variantId: Number(AddonPicker._variantIdOf(card))
            };
          });
      }

      clearSelections() {
        this.cards.forEach(function (card) {
          var box = card.querySelector('[data-addon-checkbox]');
          if (box) box.checked = false;
          card.classList.remove('is-checked');
        });
      }

      markFailed(variantId) {
        var id = String(variantId);
        this.cards.forEach(function (card) {
          var select = card.querySelector('[data-addon-variant]');
          var offers = String(AddonPicker._variantIdOf(card)) === id ||
            (select && Array.prototype.some.call(select.options, function (o) { return o.value === id; }));
          if (!offers) return;
          var box = card.querySelector('[data-addon-checkbox]');
          if (box) {
            box.checked = false;
            box.disabled = true;
          }
          if (select) select.disabled = true;
          card.classList.remove('is-checked');
          card.classList.add('is-failed');
          card.setAttribute('aria-disabled', 'true');
        });
      }

      setMessage(text) {
        var el = this.querySelector('[data-addon-message]');
        if (!el) return;
        el.textContent = text || '';
        el.hidden = !text;
      }

      titleForVariant(variantId) {
        var id = String(variantId);
        var card = this.cards.filter(function (c) {
          return String(AddonPicker._variantIdOf(c)) === id;
        })[0];
        var title = card && card.querySelector('.addon-card__title');
        return title ? title.textContent.trim() : '';
      }

      refresh() {
        var self = this;
        // Only the latest request may apply its result (responses can arrive out of order).
        var seq = (this._refreshSeq = (this._refreshSeq || 0) + 1);
        return fetch(cartUrl() + (cartUrl().indexOf('?') === -1 ? '?' : '&') + 't=' + Date.now(), {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            if (seq !== self._refreshSeq) return cart;
            self._applyCart(cart);
            return cart;
          })
          .catch(function () {
            // Leave the current state; a failed cart read must never break the page.
          });
      }

      // In-cart hiding from a cart object (mms:cart-ready / mms:cart-changed, or refresh()).
      _applyCart(cart) {
        if (!cart || !Array.isArray(cart.items)) return;
        this._cartSeen = true;
        // A newer cart supersedes any refresh() still in flight.
        this._refreshSeq = (this._refreshSeq || 0) + 1;
        var ids = new Set();
        cart.items.forEach(function (item) { ids.add(String(item.product_id)); });
        this.inCartProductIds = ids;
        this._apply();
      }

      // ---------- Impressions ----------
      // One request per page view, when at least half of the visible block is in the
      // viewport. Never in the theme editor, never for a hidden block or zero visible
      // cards, and never when the block's "Impression endpoint" setting is blank
      // (data-impression-url is then not rendered). Errors are swallowed: impressions
      // must never affect the page.

      _armImpression() {
        var self = this;
        if (this._impressionSent || this._impressionObserver) return;
        if (!this.dataset.impressionUrl) return;
        if (window.Shopify && window.Shopify.designMode) return;
        if (typeof IntersectionObserver !== 'function') return;
        try {
          this._impressionObserver = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.5) self._sendImpression();
            });
          }, { threshold: 0.5 });
          this._impressionObserver.observe(this);
        } catch (e) {
          // ignore
        }
      }

      _sendImpression() {
        if (this._impressionSent) return;
        if (this.hidden || this.blockEl.hidden || !this.isConnected) return;
        var shown = Array.prototype.slice.call(this.querySelectorAll('[data-addon-card]'))
          .filter(function (card) { return !card.hidden; })
          .map(function (card) { return Number(card.dataset.productId); });
        if (!shown.length) return;

        this._impressionSent = true;
        if (this._impressionObserver) this._impressionObserver.disconnect();

        // Shared sender (assets/mms-cart.js): adds v and visitor_id (mms_vid), keepalive
        // text/plain POST, never in the theme editor, errors swallowed.
        if (!window.MMSCart || !window.MMSCart.sendImpression) return;
        var payload = {
          product_id: Number(this.dataset.productId),
          rule_handle: this.dataset.ruleHandle,
          shown_product_ids: shown
        };
        if (this.dataset.template) payload.template = this.dataset.template;
        window.MMSCart.sendImpression(this.dataset.impressionUrl, payload);
      }

      // ---------- Internals ----------

      static _variantIdOf(card) {
        var select = card.querySelector('[data-addon-variant]');
        return select ? select.value : card.dataset.variantId;
      }

      _apply() {
        var self = this;
        var shown = 0;
        this.sequence.forEach(function (card) {
          var inCart = self.inCartProductIds.has(card.dataset.productId);
          var show = !inCart && shown < self.maxShown;
          if (show) shown++;
          if (card.hidden === show) card.hidden = !show;
          if (inCart) {
            var box = card.querySelector('[data-addon-checkbox]');
            if (box && box.checked) {
              box.checked = false;
              card.classList.remove('is-checked');
            }
          }
          if (show && !card._imgEager) {
            var img = card.querySelector('img[loading="lazy"]');
            if (img) img.loading = 'eager';
            card._imgEager = true;
          }
        });
        // Keep the visible cards in sequence order (matters for random order).
        if (this.order === 'random') {
          var row = this.querySelector('[data-addon-row]');
          this.sequence.forEach(function (card) { if (!card.hidden) row.appendChild(card); });
        }
        this.visibleCount = shown;
        var hideBlock = shown === 0 || !this.mainAvailable;
        this.blockEl.hidden = hideBlock;
        if (this.blockEl !== this) this.hidden = hideBlock;
      }

      _bindCards() {
        this.cards.forEach(function (card) {
          var box = card.querySelector('[data-addon-checkbox]');
          if (box) {
            box.addEventListener('change', function () {
              card.classList.toggle('is-checked', box.checked);
            });
          }
          // Image + title open quick-look with an action that toggles this card's checkbox
          // and fires the same change event a click on the checkbox would.
          var view = card.querySelector('[data-addon-view]');
          if (view && box) {
            view.addEventListener('click', function (evt) {
              if (!window.MMSQuickLook) return;
              evt.preventDefault();
              window.MMSQuickLook.open(card.dataset.quickLookHandle, {
                opener: view,
                // The card's selected variant: quick-look starts on its image and title,
                // and the action below ticks this card, which keeps that same variant.
                variantId: AddonPicker._variantIdOf(card),
                action: {
                  label: 'Add with my order',
                  activeLabel: 'Remove from my order',
                  isActive: function () { return box.checked; },
                  run: function () {
                    if (box.disabled) return;
                    box.checked = !box.checked;
                    box.dispatchEvent(new Event('change', { bubbles: true }));
                  }
                }
              });
            });
          }
          var select = card.querySelector('[data-addon-variant]');
          if (select) {
            select.addEventListener('change', function () {
              var opt = select.options[select.selectedIndex];
              if (!opt) return;
              var current = card.querySelector('[data-addon-price-current]');
              if (current && opt.dataset.price) current.textContent = opt.dataset.price;
              var compare = card.querySelector('[data-addon-price-compare]');
              if (compare) {
                compare.textContent = opt.dataset.compare || '';
                compare.hidden = !opt.dataset.compare;
              }
              // Photo follows the variant (Liquid already fell back to the product image).
              var img = card.querySelector('.addon-card__media img');
              if (img && opt.dataset.imgSrc) {
                img.srcset = opt.dataset.imgSrcset || '';
                img.src = opt.dataset.imgSrc;
              }
              if (img && opt.dataset.imgAlt) img.alt = opt.dataset.imgAlt;
            });
          }
        });
      }

      _bindCartEvents() {
        var self = this;
        this._onCartEvent = function (evt) {
          var cart = evt && evt.detail && evt.detail.cart;
          if (!cart) return;
          var first = !self._cartSeen;
          self._applyCart(cart);
          if (first) self._armImpression();
        };
        CART_EVENTS.forEach(function (name) {
          document.addEventListener(name, self._onCartEvent);
        });
      }

      // Impulse's theme.Variants dispatches 'variantChange' on the product section
      // container ([data-section-type="product"]) with detail.variant (null when the
      // chosen options match no variant). Listening on the container, not document,
      // ignores quick-shop modals for other products.
      _bindVariantChange() {
        var self = this;
        this._variantContainer = this.closest('[data-section-type="product"]');
        if (!this._variantContainer) return;
        this._onVariantChange = function (evt) {
          var variant = evt.detail && evt.detail.variant;
          self.mainAvailable = !!(variant && variant.available);
          self._apply();
        };
        this._variantContainer.addEventListener('variantChange', this._onVariantChange);
      }

      // Seal Subscriptions renders its widget into .sealsubs-target-element inside the
      // product form, with a hidden <input name="selling_plan" data-sls-selling_plan>.
      // Seal sets that input's value attribute to the plan ID when a subscription option
      // is chosen and to '' for one-time purchase. The widget is injected after load, so
      // the whole form is observed.
      _bindSubscription() {
        var self = this;
        var formId = this.dataset.formId;
        this._form = formId ? document.getElementById(formId) : null;
        if (!this._form || !this.oneTimeNote) return;
        var update = function () {
          var input = self._form.querySelector('input[name="selling_plan"]');
          var on = !!(input && input.value);
          self.oneTimeNote.hidden = !on;
        };
        this._onFormChange = function () { setTimeout(update, 0); };
        this._form.addEventListener('change', this._onFormChange);
        this._formObserver = new MutationObserver(update);
        this._formObserver.observe(this._form, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['value']
        });
        update();
      }
    }
  );

  // ---------------------------------------------------------------------------
  // Add to Cart with add-ons (spec: "Adding to the cart")
  // ---------------------------------------------------------------------------

  var GENERIC_ERROR = 'Something went wrong adding to your cart. Please check your connection and try again.';

  // The picker for this form: same data-form-id, and in the same product section.
  function pickerForForm(form) {
    var formId = form.getAttribute('id');
    if (!formId) return null;
    var section = form.closest('[data-section-type="product"]');
    var pickers = document.querySelectorAll('addon-picker');
    for (var i = 0; i < pickers.length; i++) {
      var p = pickers[i];
      if (p.dataset.formId !== formId) continue;
      if (section && p.closest('[data-section-type="product"]') !== section) continue;
      return p;
    }
    return null;
  }

  // Impulse's AjaxProduct error markup: <div class="errors text-center">message</div> appended to the form.
  function showFormError(form, message) {
    clearFormError(form);
    var div = document.createElement('div');
    div.classList.add('errors', 'text-center');
    div.textContent = message;
    form.append(div);
  }

  function clearFormError(form) {
    var errors = form.querySelector('.errors');
    if (errors) errors.remove();
  }

  function formFields(form) {
    var data = new FormData(form);
    var params = new URLSearchParams();
    data.forEach(function (value, key) {
      if (typeof value === 'string') params.append(key, value);
    });
    return params;
  }

  // One "add" event per add-on that made it into the cart (add-on reporting attribution;
  // MMSCart.sendAddEvents reads the cart token after the add). The main product is not an add-on.
  function addEventsFor(added, picker) {
    var source = Number(picker.dataset.productId);
    var rule = picker.dataset.ruleHandle;
    return added.map(function (s) {
      return {
        surface: 'pdp',
        rule_handle: rule,
        variant_id: Number(s.variantId),
        product_id: Number(s.productId) || undefined,
        quantity: 1,
        source_product_id: source
      };
    });
  }

  function addWithAddons(form, picker, selections) {
    var MMSCart = window.MMSCart;
    var button = form.querySelector('.add-to-cart') || form.querySelector('[type="submit"]');
    var fields = formFields(form);
    var mainProduct = null;
    var mainAdded = false;
    var failed = [];
    var added = [];

    form._addonBusy = true;
    if (MMSCart.beginBusy) MMSCart.beginBusy();
    if (button) button.classList.add('btn--loading');
    clearFormError(form);
    picker.setMessage('');

    function addonItems(list) {
      return list.map(function (s) { return { id: s.variantId, quantity: 1 }; });
    }

    // Adds `list`; on success its entries count as added.
    function addAddons(list) {
      return MMSCart.addItems(addonItems(list)).then(function () {
        added = added.concat(list);
      });
    }

    function addOneByOne(list) {
      return list.reduce(function (chain, s) {
        return chain.then(function () {
          return addAddons([s]).catch(function () { failed.push(s); });
        });
      }, Promise.resolve());
    }

    function finishSuccess() {
      // Same detail shape as Impulse's AjaxProduct.success(); the main product form has no
      // scopedEventId, so Impulse fires no scoped event for it either.
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: { product: mainProduct, addToCartBtn: button }
      }));
      picker.clearSelections();
      failed.forEach(function (s) { picker.markFailed(s.variantId); });
      if (failed.length) {
        var names = failed.map(function (s) { return picker.titleForVariant(s.variantId) || 'an add-on'; });
        picker.setMessage("Couldn't add " + names.join(', ') + '. Your other items were added.');
      }
      var events = added.length && MMSCart.sendAddEvents
        ? MMSCart.sendAddEvents(addEventsFor(added, picker), { url: picker.dataset.impressionUrl })
        : Promise.resolve();
      if (document.body.classList.contains('template-cart')) {
        // The page reloads: let the add events leave first (sendAddEvents waits at most ~1.5 s).
        events.then(function () {
          window.scrollTo(0, 0);
          location.reload();
        });
      }
    }

    return Promise.resolve()
      .then(function () {
        // 1. One combined request: main form fields + add-ons.
        return MMSCart.addItems(addonItems(selections), { fields: fields })
          .then(function (result) {
            mainAdded = true;
            mainProduct = result.data;
            added = selections.slice();
          })
          .catch(function (err) {
            if (err.type !== 'cart') throw err;
            // 2. All-or-nothing 422: add the main item alone.
            return MMSCart.addItems([], { fields: fields }).then(function (result) {
              mainAdded = true;
              mainProduct = result.data;
              // 3. Then the add-ons as a batch, falling back to one at a time.
              return addAddons(selections).catch(function () {
                return addOneByOne(selections);
              });
            });
          });
      })
      .then(finishSuccess)
      .catch(function (err) {
        if (mainAdded) {
          // Main item is in the cart; a later add-on request failed on the network.
          selections.forEach(function (s) { if (failed.indexOf(s) === -1) failed.push(s); });
          finishSuccess();
          return;
        }
        if (err && err.type === 'cart') {
          // Main item refused on its own: Impulse's error markup and event; add nothing.
          showFormError(form, err.description || err.message);
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: { errorMessage: err.description || err.message }
          }));
        } else {
          showFormError(form, GENERIC_ERROR);
        }
      })
      .finally(function () {
        form._addonBusy = false;
        if (MMSCart.endBusy) MMSCart.endBusy();
        if (button) button.classList.remove('btn--loading');
      });
  }

  function onSubmit(evt) {
    var form = evt.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!window.MMSCart) return;
    var picker = pickerForForm(form);
    if (!picker || typeof picker.getSelections !== 'function') return;

    // Ignore submits while our own sequence runs, before checking selections:
    // clearSelections() would otherwise let a fast second click through to Impulse.
    if (form._addonBusy) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      return;
    }

    var selections = picker.getSelections();
    if (!selections.length) return; // Impulse handles it exactly as before.

    evt.preventDefault();
    evt.stopImmediatePropagation();
    addWithAddons(form, picker, selections);
  }

  if (!window.__mmsAddonSubmitBound) {
    window.__mmsAddonSubmitBound = true;
    document.addEventListener('submit', onSubmit, true);
  }
})();

// Synced with the add-event attribution change (fa9b838).
